import { inject, Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { ConnectionStatus, Network } from '@capacitor/network';
import { NetworkStatus, WebConnectivityService } from '@okcargo-apps/shared-infrastructure';
import { PlatformService } from '../platform';

/**
 * @description
 * Platform agnostic implementation of the connectivity status.
 *
 * Decides where the status comes from based on the platform: the Capacitor
 * Network plugin when the app runs natively (hybrid), or the browser
 * capabilities (inherited web behaviour) when it runs in the browser. Consumers
 * only depend on `ConnectivityService`, never on the plugin.
 *
 * On native, the status is also re-checked when the app returns to the
 * foreground, since events fired while the app was in background may be missed.
 */
@Injectable()
export class CapacitorConnectivityService extends WebConnectivityService {
  private readonly platform = inject(PlatformService);

  private networkListenerHandle?: PluginListenerHandle;
  private appStateListenerHandle?: PluginListenerHandle;

  public override async start(): Promise<void> {
    if (!this.platform.isHybrid) {
      await super.start();
      return;
    }

    if (this.started) {
      return;
    }

    this.started = true;

    try {
      // The plugin is only usable once the platform is ready.
      await this.platform.ready();

      this.applyStatus(this.toNetworkStatus(await Network.getStatus()));

      this.networkListenerHandle = await Network.addListener('networkStatusChange', (status) => {
        this.applyStatus(this.toNetworkStatus(status));
      });

      this.appStateListenerHandle = await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          void this.refresh();
        }
      });

      this.registerStopOnDestroy();
    } catch (error) {
      // Never reject: the app bootstrap waits for this, and the polling/foreground re-checks can
      // still correct the status later.
      console.error('[CapacitorConnectivityService] Error starting the connectivity status:', error);
    }
  }

  protected override async refresh(): Promise<void> {
    if (!this.platform.isHybrid) {
      super.refresh();
      return;
    }

    this.applyStatus(this.toNetworkStatus(await Network.getStatus()));
  }

  protected override stop(): void {
    super.stop();
    void this.stopNativeListeners();
  }

  private async stopNativeListeners(): Promise<void> {
    await this.networkListenerHandle?.remove();
    await this.appStateListenerHandle?.remove();
    this.networkListenerHandle = undefined;
    this.appStateListenerHandle = undefined;
  }

  private toNetworkStatus(status: ConnectionStatus): NetworkStatus {
    return {
      connected: status.connected,
      connectionType: status.connectionType,
    };
  }
}
